import { logoutApi } from "@/api/services/main/auth";
import { useMyOrganization } from "@/api/services/main/organization";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { Modal } from "@/components/elements/Modal";
import { LAST_AUTH_ROUTE_KEY, LAST_EXTERNAL_AUTH_ROUTE_KEY } from "@/constants/common";
import { useTokenClaims } from "@/features/auth/hooks/useTokenClaims";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { setAxiosAuth } from "@/utils/api";
import { logout } from "@/utils/logout";
import { pickMostRecentRoute, readTimestampedRoute } from "@/utils/timestampedRoute";
import { getToken } from "@/utils/token";
import { getQueryFromURL, getSafeRedirectURL } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const CONFIRM_ROUTES: string[] = [AppRoutes.login, AppRoutes.loginCredentials];

/**
 * Guards the login routes: on `/login` and `/login/credentials`, if a valid
 * session is still active, warn the user before it is discarded (they may be
 * about to switch organizations). While that confirmation is pending a loading
 * screen is shown instead of the login content (`children`). On any other login
 * route — or with no active session — it keeps the old behavior of silently
 * clearing any leftover session so login starts fresh.
 */
export const SwitchAccountConfirmModal: FCC = ({ children }) => {
  const { accessToken } = getToken();
  const { pathname } = useLocation();
  const needsConfirm = Boolean(accessToken) && CONFIRM_ROUTES.includes(pathname);

  if (!needsConfirm) return <>{children}</>;

  return <ActiveSessionConfirm>{children}</ActiveSessionConfirm>;
};

/**
 * Rendered only when a live session exists, so `useMyOrganization` never fires
 * on the unauthenticated login screen (which would 401).
 */
const ActiveSessionConfirm: FCC = ({ children }) => {
  const { claims } = useTokenClaims();

  // Prime Axios with the cookie token so the org lookup is authorized even on a
  // full page load straight to /login (where `withAuth` never runs).
  useLayoutEffect(() => {
    setAxiosAuth();
  }, []);

  const { data: organization } = useMyOrganization();
  const [isOpen, setIsOpen] = useState(true);
  // Reveal the login content only after the user opts to log out; until then (and
  // on the "keep" branch, which navigates away) a loading screen hides it.
  const [proceedToLogin, setProceedToLogin] = useState(false);

  const organizationName = organization?.data.name ?? "";

  const query = getQueryFromURL<{ externalId?: string; redirectUrl?: string; callback?: string }>();
  const targetExternalId = query?.externalId;
  // Target org is the same one already logged in: there is nothing to switch to,
  // so behave exactly like pressing "keep" (cancel) — no modal, just navigate back.
  const isSameOrg = Boolean(targetExternalId) && targetExternalId === claims.externalId;

  const handleLogout = async () => {
    const { refreshToken } = getToken();
    if (refreshToken) await logoutApi({ refreshToken }).catch(() => 0); // error -> do nothing, just clear token
    logout();
    setIsOpen(false);
    setProceedToLogin(true);
  };

  const handleKeep = () => {
    setIsOpen(false);
    const lastSavedRoute = pickMostRecentRoute(readTimestampedRoute(LAST_AUTH_ROUTE_KEY), readTimestampedRoute(LAST_EXTERNAL_AUTH_ROUTE_KEY));
    const redirect = getSafeRedirectURL(lastSavedRoute);
    window.location.href = redirect;
  };

  // Same org → auto-run the cancel path without ever showing the modal.
  useLayoutEffect(() => {
    if (isSameOrg || !targetExternalId) handleKeep();
  }, [isSameOrg]);

  if (isSameOrg) return <LoadingScreen />;

  return (
    <>
      <Modal
        isOpen={isOpen}
        width={520}
        enableCloseIcon={false}
        preventCloseBackdrop
        flexDirectionBtnAction="row-reverse"
        justifyBtnAction="center"
        labelOk="ログアウトする"
        labelClose="そのまま利用する"
        onOk={handleLogout}
        onClose={handleKeep}
      >
        <Stack py="16px">
          <Typography variant="body14" color={text.primary} component="div">
            現在ログイン中の「{organizationName}」をログアウトして、新たに組織ID「{targetExternalId}」にログインしますか？
          </Typography>
        </Stack>
      </Modal>
      {proceedToLogin ? children : <LoadingScreen />}
    </>
  );
};
